<template>
  <div
    class="vx-card mol-card-interaction aisea-p-6"
    :class="{ selected: isSelected }"
    @click="jumpToVessel(vessel)"
  >
    <vs-row class="card-content">
      <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="12">
        <span class="aisea-text-16-600-white truncate">
          {{ vesselName }}
        </span>
      </vs-col>
    </vs-row>
    <vs-row class="card-content">
      <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="12">
        <div class="vessel-state">
          <div
            class="state-lamp-circle"
            :style="{ '--color': statusLampColor }"
          >
            <div class="state-lamp-outer"></div>
            <div class="state-lamp-inner"></div>
          </div>
          <span class="aisea-text-14-400-white aisea-ml-3 truncate">
            {{ vesselOperations }}
          </span>
        </div>
      </vs-col>
    </vs-row>
    <ul>
      <li>
        <vs-row class="card-content">
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-600-white"> 船種 </span>
          </vs-col>
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-300-white truncate">
              {{ vesselCategoryName }}
            </span>
          </vs-col>
        </vs-row>
        <vs-row class="card-content">
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-600-white"> Call Sign </span>
          </vs-col>
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-300-white truncate">
              {{ callSign }}
            </span>
          </vs-col>
        </vs-row>
        <vs-row class="card-content">
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-600-white"> MMSI </span>
          </vs-col>
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-300-white truncate">
              {{ mmsi }}
            </span>
          </vs-col>
        </vs-row>
        <vs-row class="card-content">
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-600-white"> 速力 </span>
          </vs-col>
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-300-white truncate">
              {{ speed }}kn
            </span>
          </vs-col>
        </vs-row>
        <vs-row class="card-content">
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-600-white"> 船首方位 </span>
          </vs-col>
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-300-white truncate">
              {{ heading }}°
            </span>
          </vs-col>
        </vs-row>
        <vs-row class="card-content">
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-600-white"> 最終更新 </span>
          </vs-col>
          <vs-col vs-type="flex" vs-justify="start" vs-align="center" vs-w="6">
            <span class="aisea-text-12-300-white truncate">
              {{ lastReceived }}
            </span>
          </vs-col>
        </vs-row>
      </li>
    </ul>
  </div>
</template>

<script>
/**
 * 自社船・選択船の船情報を管理・表示するコンポーネント
 * 親からv-forなどで個別の船情報を渡して使用する。
 * @parent O.OwnVessel
 * @parent O.SelectedVessel
 * @props vessel: Object 船情報オブジェクト
 * @props jumpToVessel: Function クリックした船にマップを移動(追跡モード)
 * @props vesselPosition: Function FireStoreから取得したリアルタイムな船の位置
 * @props isSelected: Boolean 現在の選択状態を管理
 */
import { DateUtil } from "@/utils";
import * as Enum from "@/config/const.js";
import { getStatusLamp } from "@/utils/VesselUtil";

export default {
  name: "M.VesselCard",
  props: {
    vessel: {
      type: Object,
      default: null,
    },
    jumpToVessel: {
      type: Function,
    },
    vesselPosition: {
      type: Object,
    },
    isSelected: {
      type: Boolean,
    },
  },
  data() {
    return {
      defaultValues: {
        noneNumber: "0",
        noneValue: "---",
        noneText: "",
        noneStatus: "-",
      },
    };
  },
  computed: {
    // 船名
    vesselName() {
      if (this.vessel) {
        if (this.vessel.name_ja !== null) {
          return this.vessel.name_ja;
        } else if (this.vessel.name !== null) {
          return this.vessel.name;
        } else {
          return this.defaultValues.noneText;
        }
      } else {
        return this.defaultValues.noneText;
      }
    },
    // 船種
    vesselCategoryName() {
      if (this.vessel) {
        return this.vessel.vessel_category.id != undefined
          ? Enum.VESSEL_CATEGORY.properties[this.vessel.vessel_category.id]
              .name_ja
          : this.defaultValues.noneValue;
      } else {
        return this.defaultValues.noneValue;
      }
    },
    // 速力
    speed() {
      if (this.vesselPosition) {
        return this.vesselPosition.v !== undefined
          ? this.vesselPosition.v
          : this.defaultValues.noneNumber;
      } else {
        return this.defaultValues.noneNumber;
      }
    },
    // 船種
    heading() {
      if (this.vesselPosition) {
        return this.vesselPosition.h !== undefined ||
          this.vesselPosition.h !== 0
          ? this.vesselPosition.h
          : this.vesselPosition.d !== undefined
          ? this.vesselPosition.d
          : this.defaultValues.noneNumber;
      } else {
        return this.defaultValues.noneNumber;
      }
    },
    // 最終更新
    lastReceived() {
      if (this.vesselPosition) {
        return this.vesselPosition.t !== undefined
          ? DateUtil.elapsedTimeByTimestamp(this.vesselPosition.t)
          : this.defaultValues.noneValue;
      } else {
        return this.defaultValues.noneValue;
      }
    },
    // 航行状況
    vesselOperations() {
      const pos = this.vesselPosition;
      if (pos == null || pos.s == null) {
        return this.defaultValues.noneStatus;
      }
      return Enum.NAVIGATION_STATUS.properties[String(pos.s)].name_ja;
    },
    // 航行状況ステータスランプ
    statusLampColor() {
      const pos = this.vesselPosition;
      if (pos == null || pos.s == null) {
        return getStatusLamp(null).color;
      }
      return getStatusLamp(pos.s).color;
    },
    // call Sign
    callSign() {
      if (this.vessel && this.vessel.call_sign) {
        return this.vessel.call_sign;
      }
      return this.defaultValues.noneValue;
    },
    // MMSI
    mmsi() {
      if (this.vessel && this.vessel.mmsi) {
        return this.vessel.mmsi;
      }
      return this.defaultValues.noneValue;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.vx-card {
  cursor: pointer;
  width: 360px;
  height: 194px;
  margin: 0 auto;

  .card-content {
    margin-bottom: 1px;
  }
}

.mol-card-interaction {
  background-color: $aisea-blue-gray;

  &:hover {
    background-color: #6a717d;
  }

  &:active {
    background-color: #969ba4;
  }
}

.mol-card-interaction.selected {
  background-color: #1b5e83;
  outline: 2px solid #2d9cdb;

  &:hover {
    background-color: #588eac;
  }

  &:active {
    background-color: #98b6c7;
  }
}

.vessel-state {
  display: flex;
  align-items: center;
  font-size: 14px;
  margin-bottom: 3px;
  .state-lamp-circle {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    .state-lamp-outer {
      width: 15px;
      height: 15px;
      opacity: 0.5;
      background: var(--color);
      border-radius: 10px;
      position: absolute;
    }
    .state-lamp-inner {
      width: 9px;
      height: 9px;
      background: var(--color);
      border-radius: 10px;
      position: absolute;
    }
  }
}
</style>
